/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { Button } from "@material-ui/core";
import Page from "components/common_components/Page/Page";
import useStyles from "../useStyles";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import ModulesContext from "../ModulesContext";
import { FormDialog } from "components/common_components/Dialogs";
import { Form as FormikForm, TextField, DatePickerField, CheckboxField, SelectField } from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PaymentIcon from "@material-ui/icons/Payment";
import Report from "../Reports/Report";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AuthRequiredDialog from "./AuthRequiredDialog";
import paymentMethods from "utils/static/paymentMethods";
import { getDateMillis } from "utils/dateUtils";
import AppContext from "AppContext";

const headCells = [
    { index: 1, id: "dateString", orderBy: "date", disablePadding: false, label: "Date" },
    { index: 2, id: "charge", disablePadding: false, type: "currency", label: "Charge", numeric: true },
    { index: 3, id: "credit", disablePadding: false, type: "currency", label: "Credit", numeric: true },
    { index: 4, id: "paymentMethodString", disablePadding: false, label: "Payment Method" },
    { index: 5, id: "user.name", disablePadding: false, label: "Responsible" },
    { index: 6, id: "description", disablePadding: false, label: "Description" },
];

function Accounting({ history, onCancel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [formInitialValues, setFormInitialValues] = useState(undefined);
    const [formDialogProps, setFormDialogProps] = useState({
        open: false,
        title: "",
        formId: "in-dialog-form",
        isSubmitting: false,
        onClose: () => {
            setFormInitialValues(undefined);
            setFormDialogProps((prev) => ({ ...prev, open: false }));
        },
    });
    const [authRequiredDialogProps, setAuthRequiredDialogProps] = useState({
        open: false,
        onAuthenticated: undefined,
        onClose: () => {
            setAuthRequiredDialogProps((prev) => ({ ...prev, open: false }));
        },
    });

    const [downloadLinkReady, setDownloadLinkReady] = useState(false);
    const [report, setReport] = useState(undefined);
    const [reportProps, setReportProps] = useState(undefined);

    const {
        Accounting,
        currentFileCase,
        getAccounting,
        accountingData: data,
        pageableAccountingData,
        balance,
        getBalanceAsync,
        isLoading,
        module,
    } = useContext(ModulesContext);

    const {
        api: {
            Settings: { InvoicingIssuers },
        },
    } = useContext(AppContext);

    useEffect(() => {
        if (!currentFileCase) {
            dispatch(notifyError("You have to select a client file first!"));
            onCancel();
        }
    }, [currentFileCase]);

    function onRowClickHandler(item) {}

    function onDeleteSuccess() {
        dispatch(notifyInfo("Removed successfully!"));
        getAccounting();
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(selectedItems) {
        setAuthRequiredDialogProps((prev) => ({
            ...prev,
            open: true,
            onAuthenticated: () => {
                Accounting.deleteAll({
                    ids: selectedItems,
                    onSuccess: onDeleteSuccess,
                    onError: onDeleteError,
                });
            },
        }));
    }

    const validationSchema = Yup.lazy((values) => {
        return Yup.object({
            date: Yup.date().required("Required field"),
            concept: Yup.string().required("Required field"),
            paymentMethod: values.concept === "Credit" ? Yup.string().required("Required field") : Yup.string().notRequired().nullable(),
        });
    });

    function showAddConcept(concept) {
        setFormInitialValues({
            date: new Date(),
            charge: 0,
            credit: 0,
            userId: user.id,
            description: concept === "Payment" ? "Partial Payment" : "Charge for Services",
            concept: concept === "Payment" ? "Credit" : concept,
            paymentMethod: concept === "Payment" ? "cash" : null,
            clientId: currentFileCase.client.id,
            printReceipt: concept === "Payment" ? true : false,
            printInvoice: concept === "Payment" ? false : true,
        });
        setFormDialogProps((prev) => ({ ...prev, open: true, title: `Add ${concept}` }));
    }

    function addConcept(concept) {
        if (concept === "Payment") {
            setAuthRequiredDialogProps((prev) => ({
                ...prev,
                open: true,
                onAuthenticated: () => showAddConcept(concept),
            }));
        } else {
            showAddConcept(concept);
        }
    }

    async function onSuccess({ id }, printReceipt, printInvoice, setSubmitting) {
        setSubmitting(false);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: false }));
        dispatch(notifySuccess(`Transaction completed successfully!`));
        formDialogProps.onClose();
        getAccounting();
        if (printReceipt) {
            const receipts = await Accounting.getAccountingReceiptsAsync(id);
            const newBalance = await getBalanceAsync();
            if (receipts && receipts.length > 0) {
                setReportProps({
                    personalInfo: currentFileCase,
                    receiptsData: receipts,
                    currentUser: user,
                    balance: newBalance,
                    isTaxes: module.dbName === "taxes",
                });
                setReport("receipt");
                setDownloadLinkReady(true);
            }
        }
        if (printInvoice) {
            try {
                const invoice = await Accounting.getAccountingInvoiceAsync(id);
                if (invoice) {
                    setReportProps({
                        personalInfo: currentFileCase,
                        invoice,
                        currentUser: user,
                    });
                    setReport("invoice");
                    setDownloadLinkReady(true);
                } else {
                    dispatch(notifyError(`No Invoice found.`));
                }
            } catch (error) {
                dispatch(notifyError(`Unexpected error getting Invoice.`));
            }
        }
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            dispatch(notifyError(error));
            formDialogProps.onClose();
        }
        setSubmitting(false);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: false }));
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { date, charge, credit, printReceipt, printInvoice, ...payload } = values;

        if (payload.concept !== "Credit" && payload.concept !== "Charge") {
            setErrors({ concept: `The concept ${payload.concept} is not valid. Use Charge or Credit.` });
            return;
        }

        if (payload.concept === "Credit") {
            payload.charge = 0;
            payload.credit = Number(credit);
            if (Number(credit) <= 0) {
                setErrors({ credit: `You can't apply $ ${credit} as a payment.` });
                return;
            }
            const newBalance = Number(balance) - Number(credit);
            payload.balance = newBalance;
            // TODO: Validate whether this restriction applies or not in the future. A configuration variable could be created to control this scenario.
            // if (newBalance < 0) {
            //     setErrors({ credit: "You can't apply this payment. The new balance would be less than $ 0." });
            //     return;
            // }
        }

        if (payload.concept === "Charge") {
            payload.credit = 0;
            payload.charge = Number(charge);
            if (Number(charge) <= 0) {
                setErrors({ charge: `You can't apply $ ${charge} as a charge.` });
                return;
            }
            const newBalance = Number(balance) + Number(charge);
            payload.balance = newBalance;
        }

        const dateInMillis = getDateMillis(date);

        setSubmitting(true);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: true }));

        Accounting.add({
            payload: {
                ...payload,
                date: dateInMillis,
                fileCaseId: currentFileCase.id,
            },
            onSuccess: (response) => onSuccess(response, printReceipt, printInvoice, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function onCancelHandler() {
        setReport(undefined);
        setReportProps(undefined);
        setDownloadLinkReady(false);
    }

    const getDocumentIssuer = async () => {
        try {
            return await InvoicingIssuers.getAsync();
        } catch {
            return null;
        }
    };

    async function generateStatement() {
        try {
            const issuer = await getDocumentIssuer();
            const statementData = await Accounting.getAllAsync(currentFileCase.id);
            setReportProps({
                personalInfo: currentFileCase,
                accountingdata: statementData,
                currentUser: user,
                balance: balance,
                isTaxes: module.dbName === "taxes",
                issuer,
            });
            setReport("statement");
            setDownloadLinkReady(true);
        } catch (error) {
            dispatch(notifyError("An error occurred while getting the statement data!"));
        }
    }

    function getPaymentMethods() {
        return paymentMethods.filter((pm) => (pm.restricted && user.isAdmin) || !pm.restricted);
    }

    function filterBuilder(filterValue) {
        return filterValue
            ? {
                  description: filterValue,
              }
            : {};
    }

    const viewerEnabled = Boolean(downloadLinkReady && report && reportProps);

    return (
        <Page className={classes.root}>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    <Grid container spacing={3} style={{ justifyContent: "space-between" }}>
                        <Grid item md={6}>
                            {!viewerEnabled && (
                                <>
                                    {currentFileCase.client.enabled && (
                                        <>
                                            <Button
                                                variant="text"
                                                style={{ marginRight: 20 }}
                                                size="small"
                                                color="secondary"
                                                startIcon={<AttachMoneyIcon />}
                                                onClick={() => addConcept("Charge")}
                                            >
                                                Add Charge
                                            </Button>
                                            <Button
                                                variant="text"
                                                style={{ marginRight: 20 }}
                                                size="small"
                                                color="primary"
                                                startIcon={<PaymentIcon />}
                                                onClick={() => addConcept("Payment")}
                                            >
                                                Add Payment
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        variant="text"
                                        style={{ marginRight: 20 }}
                                        size="small"
                                        color="default"
                                        startIcon={<PictureAsPdfOutlinedIcon />}
                                        onClick={generateStatement}
                                    >
                                        Statement to PDF
                                    </Button>
                                </>
                            )}
                            {viewerEnabled && (
                                <>
                                    <Button
                                        onClick={onCancelHandler}
                                        variant="text"
                                        size="small"
                                        type="reset"
                                        color="default"
                                        startIcon={<ArrowBackIcon />}
                                    >
                                        Exit Report View
                                    </Button>
                                </>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {downloadLinkReady ? (
                                <>{viewerEnabled && <Report reportProps={reportProps} reportType={report} />}</>
                            ) : (
                                <EnhancedTable
                                    headCells={headCells}
                                    data={data}
                                    title="Accounting"
                                    defaultOrder={{ order: "desc", orderBy: "dateString" }}
                                    keyColumnName="id"
                                    selectable={user.isAdmin}
                                    onRowClick={onRowClickHandler}
                                    onDelete={onDeleteHandler}
                                    deletable={user.isAdmin}
                                    onRequestData={getAccounting}
                                    filterBuilder={filterBuilder}
                                    pageableData={pageableAccountingData}
                                    defaultRowsPerPage={15}
                                />
                            )}
                        </Grid>
                    </Grid>
                    <FormDialog {...formDialogProps}>
                        {formInitialValues && (
                            <FormikForm
                                id="in-dialog-form"
                                onSubmit={onSubmitHandler}
                                validateOnChange={false}
                                initialValues={formInitialValues}
                                validationSchema={validationSchema}
                                showButtons={false}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={5}>
                                        <DatePickerField fieldName="date" label="Date *" />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField disabled fieldName="concept" label="Concept" />
                                    </Grid>
                                    {formInitialValues.concept === "Charge" && (
                                        <>
                                            <Grid item xs={4}>
                                                <TextField fieldName="charge" label="Charge *" currency />
                                            </Grid>
                                            <Grid item xs={8}>
                                                <CheckboxField fieldName="printInvoice" label="Print Invoice" />
                                            </Grid>
                                        </>
                                    )}
                                    {formInitialValues.concept === "Credit" && (
                                        <>
                                            <Grid item xs={4}>
                                                <TextField fieldName="credit" label="Credit *" currency />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <SelectField
                                                    fieldName="paymentMethod"
                                                    label="Payment Method *"
                                                    options={getPaymentMethods()}
                                                />
                                            </Grid>
                                            <Grid item xs={8}>
                                                <CheckboxField fieldName="printReceipt" label="Print Receipt" />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <TextField fieldName="description" label="Description" multiline minRows={3} />
                                    </Grid>
                                </Grid>
                            </FormikForm>
                        )}
                    </FormDialog>
                    <AuthRequiredDialog {...authRequiredDialogProps} />
                </>
            )}
        </Page>
    );
}

Accounting.propTypes = {
    history: PropTypes.shape({
        replace: PropTypes.func,
        location: PropTypes.shape({
            pathname: PropTypes.string,
        }),
    }).isRequired,
};

export default withRouter(Accounting);
